import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Player, CardType, HeroId } from '../types';
import Card from './Card';
import AnimatedCounter from './AnimatedCounter';
import { HealthIcon, ShieldIcon } from '../constants';
import { useAsset } from '../hooks/useAsset';
import veriskAvatar from '../assets/custom/VeriskAvatar.png';
import erokBuruAvatar from '../assets/custom/Erok-BuruAvatar.png';
import vergAvatar from '../assets/custom/VergAvatar.png';
import astridAvatarPng from '../assets/custom/AstridAvatar.png';
import ferrimAvatar from '../assets/custom/FerrimAvatar.png';
import norimAvatar from '../assets/custom/NorimAvatar.png';
import evelionAvatar from '../assets/custom/EvelionAvatar.png';
import warriorPng from '../assets/custom/Warrior.png';
import magePng from '../assets/custom/Mage.png';
import occultistPng from '../assets/custom/Occultist.png';
import corilAvatar from '../assets/custom/CorilAvatar.png';
import terrikAvatar from '../assets/custom/TerrikAvatar.png';
import nyrixAvatar from '../assets/custom/NyrixAvatar.png';
import shanuAvatar from '../assets/custom/ShanuAvatar.png';
import lorianAvatar from '../assets/custom/LorianAvatar.png';
import gemataAvatar from '../assets/custom/GemataAvatar.png';

// Emotes
import { EmoteBubble } from './emotes/EmoteBubble';
import { EmoteMenu } from './emotes/EmoteMenu';
import { emoteService } from '../services/emoteService';
import { useParticles } from '../contexts/ParticleContext';
import { isTouchDevice } from '../services/device';
import { Language, t } from '../data/translations';
import { getHeroDescription, HERO_DATA } from '../data/heroData';
import { useTouchHint } from '../hooks/useTouchHint';
import { useShellLayout } from '../hooks/useShellLayout';

const PlayerAvatar: React.FC<{
    player: Player;
    isOpponent?: boolean;
    forwardedRef: React.Ref<HTMLDivElement>;
    effectCardRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
    isAnimating: (cardId: string) => boolean;
    onAvatarClick?: () => void;
    language: Language;
}> = ({ player, isOpponent = false, forwardedRef, effectCardRefs, isAnimating, onAvatarClick, language }) => {
    // Get particle layer from global context
    const particleLayerRef = useParticles();
    const { isMobileShell } = useShellLayout();
    const [isHovered, setIsHovered] = useState(false);

    // Hero Hint State
    const [showHeroHint, setShowHeroHint] = useState(false);
    const [hintTrigger, setHintTrigger] = useState<'hover' | 'longpress' | null>(null);
    const hintHoverTimer = useRef<NodeJS.Timeout | null>(null);

    // Emote State
    const [showEmoteMenu, setShowEmoteMenu] = useState(false);
    const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
    const [activeEmote, setActiveEmote] = useState<{ id: string, type: 'premium' | 'animated' } | null>(null);

    // Load emoteSlots from localStorage for local player
    const [localEmoteSlots, setLocalEmoteSlots] = useState<{ id: string, type: 'premium' | 'animated' }[]>(() => {
        // Priority 1: Server Data (Real-time)
        if (player.emoteSlots && player.emoteSlots.length > 0) {
            return player.emoteSlots;
        }

        // Priority 2: Local Cache (Self only)
        if (!isOpponent) {
            try {
                const authRaw = localStorage.getItem('oko_auth');
                if (authRaw) {
                    const authData = JSON.parse(authRaw);
                    if (authData.emoteSlots && Array.isArray(authData.emoteSlots) && authData.emoteSlots.length === 6) {
                        return authData.emoteSlots;
                    }
                }
            } catch (e) {
                console.warn('[PlayerAvatar] Failed to read emoteSlots from localStorage:', e);
            }
        }
        // Fallback: Default slots
        return [
            { id: 'PIECE', type: 'premium' },
            { id: 'SMIRK', type: 'premium' },
            { id: 'ANGER', type: 'premium' },
            { id: 'SAD', type: 'premium' },
            { id: 'SHOCKED', type: 'premium' },
            { id: 'THANKS', type: 'premium' }
        ];
    });

    // React to server updates (Hydration/Sync)
    useEffect(() => {
        if (player.emoteSlots && player.emoteSlots.length > 0) {
            setLocalEmoteSlots(player.emoteSlots);
        }
    }, [player.emoteSlots]);

    // Double-tap detection for touch devices
    const lastTapTimeRef = useRef(0);

    // Touch handlers for long-press hint (2 seconds)
    const touch = useTouchHint({
        onLongPress: () => {
            setShowHeroHint(true);
            setHintTrigger('longpress');
        },
        longPressDelay: 2000
    });

    // Subscribe to Emotes
    useEffect(() => {
        return emoteService.onEmote((playerId, emoteId, emoteType) => {
            // Loose comparison because playerId comes from server as string usually
            if (String(playerId) === String(player.id)) {
                setActiveEmote({ id: emoteId, type: emoteType });
            }
        });
    }, [player.id]);

    const handleAvatarMouseEnter = () => {
        setIsHovered(true);
        // Start 2s timer for mouse hover hint
        if (hintHoverTimer.current) clearTimeout(hintHoverTimer.current);
        hintHoverTimer.current = setTimeout(() => {
            setShowHeroHint(true);
            setHintTrigger('hover');
        }, 2000);
    };

    const handleAvatarMouseLeave = () => {
        setIsHovered(false);
        if (hintHoverTimer.current) {
            clearTimeout(hintHoverTimer.current);
            hintHoverTimer.current = null;
        }
        // Hide hint on mouse leave if it was a hover hint
        if (hintTrigger === 'hover') {
            setShowHeroHint(false);
            setHintTrigger(null);
        }
    };

    const handleAvatarClick = (e: React.MouseEvent) => {
        if (touch.interceptClick(e)) return;
        
        if (isOpponent) {
            setShowHeroHint(true);
            setHintTrigger('longpress');
        } else {
            setMenuPos({ x: e.clientX, y: e.clientY });
            setShowEmoteMenu(true);
        }

        onAvatarClick?.();
    };

    const player1Avatar = useAsset('avatars.player1');
    const player2Avatar = useAsset('avatars.player2');
    const frameSrc = useAsset('avatars.frame');

    const getAvatarSrc = (isOpp: boolean) => {
        const hId = player.hero?.toUpperCase().replace(/-/g, '_');
        if (hId && HERO_DATA[hId]) return HERO_DATA[hId].avatar;
        return isOpp ? player2Avatar : player1Avatar;
    };

    const getHeroFullArt = () => {
        const hId = player.hero?.toUpperCase().replace(/-/g, '_');
        if (hId && HERO_DATA[hId]) return HERO_DATA[hId].card;
        return null;
    };
    const glowClass = player.effectCards?.some(c => c.type === CardType.Hibernation) ? 'shadow-[0_0_25px_rgba(236,72,153,0.8)] rounded-full' : '';
    const containerClass = isMobileShell ? 'w-40 h-24' : 'w-52 h-32';
    const avatarClass = isMobileShell ? 'w-20 h-20' : 'w-24 h-24';
    const namePlateClass = isMobileShell ? 'px-2.5 py-0.5 text-sm leading-4 max-w-[8.5rem] truncate' : 'px-5 py-1 text-lg';
    const statAnchorClass = isMobileShell ? (isOpponent ? 'top-6' : 'bottom-6') : (isOpponent ? 'top-8' : 'bottom-8');
    const statIconClass = isMobileShell ? 'w-7 h-7' : 'w-8 h-8';
    const shieldIconClass = isMobileShell ? 'absolute w-9 h-9 text-blue-500 opacity-70' : 'absolute w-10 h-10 text-blue-500 opacity-70';
    const counterFontSize = isMobileShell ? 20 : 24;

    const avatarRect = (forwardedRef as React.RefObject<HTMLDivElement>)?.current?.getBoundingClientRect();
    const rectData = avatarRect ? { x: avatarRect.left, y: avatarRect.top, w: avatarRect.width, h: avatarRect.height } : undefined;
    const heroNameDisplay = player.hero ? player.hero.replace(/_/g, ' ') : '';
    const heroHintOverlay = showHeroHint ? (
        <div
            className={`fixed inset-0 z-[1000] flex items-center justify-center animate-[fadeIn_0.3s_ease-out] ${hintTrigger === 'longpress' ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'}`}
            onClick={() => hintTrigger === 'longpress' && setShowHeroHint(false)}
        >
            {hintTrigger === 'longpress' && <div className="absolute inset-0 bg-black/45 backdrop-blur-sm" />}

            <div
                className={`relative bg-slate-900 shadow-[0_0_30px_rgba(0,0,0,0.9)] rounded-2xl border-2 border-slate-700 pointer-events-auto ${isMobileShell ? 'flex flex-row items-stretch w-[min(90vw,540px)] max-h-[85dvh] p-3 gap-4' : 'flex flex-col items-center max-w-sm p-6'}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={`relative ${isMobileShell ? 'w-[42%] min-h-[180px] flex-shrink-0 flex-grow-0 rounded-xl' : 'w-full aspect-[3/4] mb-4 rounded-lg'} overflow-hidden border border-slate-700 shadow-md`}>
                    {getHeroFullArt() ? (
                        <img src={getHeroFullArt()!} alt={player.hero} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full bg-slate-800 flex items-center justify-center text-slate-500">No Image</div>
                    )}
                </div>
                
                <div className={`flex flex-col ${isMobileShell ? 'w-[58%] justify-between py-1' : 'w-full items-center justify-center'}`}>
                    <div className="flex flex-col">
                        <h3 className={`${isMobileShell ? 'text-xl mb-2 text-left' : 'text-2xl mb-2 text-center'} font-bold text-amber-200 tracking-widest uppercase`}>
                            {heroNameDisplay}
                        </h3>
                        <div className={`w-full overflow-y-auto custom-scrollbar pr-2 ${isMobileShell ? 'max-h-[50dvh]' : ''}`}>
                            <p className={`${isMobileShell ? 'text-sm leading-relaxed text-left' : 'text-sm leading-relaxed text-center'} text-slate-200`}>
                                {getHeroDescription(player.hero || '', language)}
                            </p>
                        </div>
                    </div>
                    {hintTrigger === 'longpress' && (
                        <div className={`mt-3 text-slate-400 text-xs animate-pulse lowercase ${isMobileShell ? 'text-left' : 'text-center'} w-full`}>
                            {t('ui.tapToClose', language) || 'Tap to close'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    ) : null;

    return (
        <div
            id={`avatar-${player.id}`}
            ref={forwardedRef}
            className={`${containerClass} flex justify-center items-center relative`}
        >
            {/* Emote Bubble */}
            {activeEmote && (
                <EmoteBubble
                    emoteId={activeEmote.id}
                    type={activeEmote.type}
                    isOpponent={!!isOpponent}
                    onComplete={() => setActiveEmote(null)}
                    avatarRect={rectData}
                />
            )}

            {/* Emote Menu */}
            {!isOpponent && showEmoteMenu && (
                <EmoteMenu
                    x={menuPos.x}
                    y={menuPos.y}
                    emoteSlots={localEmoteSlots}
                    onSelect={(id, type) => {
                        setShowEmoteMenu(false);
                        emoteService.emitEmote(String(player.id), id, type, true);
                    }}
                    onClose={() => setShowEmoteMenu(false)}
                />
            )}

            {heroHintOverlay && ReactDOM.createPortal(heroHintOverlay, document.getElementById('portal-root') || document.body)}

            <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
                {player.effectCards?.map((card, index) => {
                    const numCards = player.effectCards.length;
                    const yDirection = isOpponent ? 1 : -1;
                    const nonHoverY = 50 * yDirection;
                    const hoverY = 100 * yDirection;
                    const angle = isHovered ? (index - (numCards - 1) / 2) * 20 * (isOpponent ? -1 : 1) : 0;
                    const translateX = isHovered ? (index - (numCards - 1) / 2) * 35 : 0;
                    const translateY = isHovered ? hoverY : nonHoverY;
                    const flipAngle = isOpponent ? 180 : 0;

                    return (
                        <div
                            ref={el => { effectCardRefs.current[card.id] = el; }}
                            key={card.id}
                            className={`absolute transition-all duration-300 ease-out ${isAnimating(card.id) ? 'opacity-0' : ''}`}
                            style={{
                                transform: `scale(0.6) translateY(${translateY}px) translateX(${translateX}px) rotate(${angle + flipAngle}deg)`,
                                zIndex: 0,
                            }}
                        >
                            <Card card={card} rotation={card.type === CardType.Disease ? 180 : 0} language={language} />
                        </div>
                    );
                })}
            </div>

            <div className={`absolute left-2 ${statAnchorClass} z-20`}>
                <div className={`relative flex flex-col items-center ${isOpponent ? 'scale-y-[-1]' : ''}`}>

                    {player.shield > 0 && (
                        <div className={`${isMobileShell ? 'text-xl' : 'text-2xl'} font-bold text-blue-300 drop-shadow-lg -mb-2 z-10`} style={{ transform: isOpponent ? 'scaleY(-1)' : 'none' }}>
                            <AnimatedCounter value={player.shield} fontSize={counterFontSize} fontWeight="bold" />
                        </div>
                    )}

                    <div className={`${isMobileShell ? 'w-8 h-7' : 'w-10 h-8'} relative flex items-center justify-center`}>
                        <HealthIcon className={`${statIconClass} text-red-500`} />
                        {player.shield > 0 && (
                            <ShieldIcon className={shieldIconClass} />
                        )}
                    </div>

                    <div className={`${isMobileShell ? 'text-xl' : 'text-2xl'} font-bold text-red-400 tracking-tighter`} style={{ transform: isOpponent ? 'scaleY(-1)' : 'none' }}>
                        <AnimatedCounter value={player.health} fontSize={counterFontSize} fontWeight="bold" />
                    </div>
                </div>
            </div>


            <div className={`flex flex-col items-center z-10 ${isOpponent ? 'scale-y-[-1]' : ''}`}>
                <div
                    className={`transition-shadow duration-300 ${glowClass} group cursor-pointer`}
                    onMouseEnter={handleAvatarMouseEnter}
                    onMouseLeave={handleAvatarMouseLeave}
                    onClick={handleAvatarClick}
                    {...touch}
                >
                    <div className={`${avatarClass} rounded-full relative -mb-2`}>
                        <img src={getAvatarSrc(isOpponent)} alt="avatar" className="absolute inset-0 w-full h-full object-cover rounded-full" />
                        {player.playerClass === 'WARRIOR' && (
                            <img src={warriorPng} alt="class" className="absolute inset-0 w-full h-full object-cover rounded-full transition-opacity duration-300 opacity-0 group-hover:opacity-100" />
                        )}
                        {player.playerClass === 'MAGE' && (
                            <img src={magePng} alt="class" className="absolute inset-0 w-full h-full object-cover rounded-full transition-opacity duration-300 opacity-0 group-hover:opacity-100" />
                        )}
                        {player.playerClass === 'OCCULTIST' && (
                            <img src={occultistPng} alt="class" className="absolute inset-0 w-full h-full object-cover rounded-full transition-opacity duration-300 opacity-0 group-hover:opacity-100" />
                        )}
                        <img src={frameSrc} alt="frame" className="absolute inset-0 w-full h-full object-cover rounded-full" />
                    </div>
                </div>
                <div className={`bg-slate-900/80 rounded-md text-white font-bold z-10 border-2 border-slate-700 ${namePlateClass}`} style={{ transform: isOpponent ? 'scaleY(-1)' : 'none' }}>
                    {player.name}
                </div>
            </div>

            {
                isOpponent && (
                    <div className={`flex flex-col items-center absolute right-2 ${statAnchorClass} z-20`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className={`${isMobileShell ? 'w-7 h-7' : 'w-8 h-8'} text-cyan-400 ${isOpponent ? 'scale-y-[-1]' : ''}`} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2L2 8.5l10 13.5L22 8.5L12 2zm0 2.34L19.66 8.5 12 18.33 4.34 8.5L12 4.34z" />
                        </svg>
                        <div className={`${isMobileShell ? 'text-xl w-7 h-7' : 'text-2xl w-8 h-8'} font-bold text-cyan-300 tracking-tighter flex items-center justify-center`}>
                            <AnimatedCounter value={player.essence} fontSize={counterFontSize} fontWeight="bold" minDigits={2} />
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default PlayerAvatar;
